import { styled } from '@sds-system/system';
import type { Theme } from '@sds-system/types';
import { getAllStyledProps } from '@sds-system/utils';
import type {
  BadgeContentWrapperStyledComponentProps,
  BadgeRootStyledComponentProps,
  BadgeRootVariantsType,
} from './Badge.types';

export const StyledBadgeRoot = styled('div', {
  componentName: 'Badge',
  slot: 'root',
})<BadgeRootStyledComponentProps>(
  () => ({
    position: 'relative',
    display: 'inline-block',
  }),
  getAllStyledProps,
);

export const StyledIconWrapper = styled('span')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

export const StyledBadgeContentWrapper = styled('div', {
  componentName: 'Badge',
  slot: 'content',
})<BadgeContentWrapperStyledComponentProps>(
  ({ theme, anchorOffset, hasPadding, hasWrappingChildren }) => ({
    position: hasWrappingChildren ? 'absolute' : 'unset',
    width: 'max-content',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.primary[500],
    color: theme.palette.text.interactive.inverse,
    overflow: 'hidden',
    ...theme.typography.caption.regular,
    variants: [
      ...getSizeStyles(theme, hasPadding || false),
      ...getVariantStyles(),
      ...(hasWrappingChildren ? getBadgePositionStyles(anchorOffset) : []),
      { props: { variant: 'circle' }, style: { ...theme.typography.caption.medium } },
    ],
  }),
  getAllStyledProps,
);

const getVariantStyles: () => BadgeRootVariantsType = () => {
  return [
    { props: { variant: 'circle' }, style: { borderRadius: '999px' } },
    { props: { variant: 'rectangle' }, style: { borderRadius: '4px' } },
    {
      props: { variant: 'smDot' },
      style: {
        width: '4px',
        height: '4px',
        borderRadius: '50%',
      },
    },
    {
      props: { variant: 'lgDot' },
      style: {
        width: '6px',
        height: '6px',
        borderRadius: '50%',
      },
    },
  ];
};

const getSizeStyles: (theme: Theme, hasPadding: boolean) => BadgeRootVariantsType = (
  theme,
  hasPadding,
) => {
  return [
    {
      props: { size: '3XS' },
      style: {
        height: `${theme.size['3XS']}px`,
        maxHeight: `${theme.size['3XS']}px`,
        minWidth: `${theme.size['3XS']}px`,
        ...(hasPadding && {
          paddingLeft: '4px',
          paddingRight: '4px',
        }),
      },
    },
    {
      props: { size: 'XS' },
      style: {
        height: `${theme.size['XS']}px`,
        maxHeight: `${theme.size['XS']}px`,
        minWidth: `${theme.size['XS']}px`,
        ...(hasPadding && {
          paddingLeft: '8px',
          paddingRight: '8px',
        }),
      },
    },
  ];
};

const getBadgePositionStyles: (
  offset: BadgeContentWrapperStyledComponentProps['anchorOffset'],
) => BadgeRootVariantsType = (
  { vertical = 0, horizontal = 0 } = { vertical: 0, horizontal: 0 },
) => {
  return [
    {
      props: { anchorOrigin: 'top.right' },
      style: {
        top: `${0 + vertical}px`,
        right: `${0 + horizontal}px`,
        transform: 'translate(50%, -50%)',
      },
    },
    {
      props: { anchorOrigin: 'top.left' },
      style: {
        top: `${0 + vertical}px`,
        left: `${0 + horizontal}px`,
        transform: 'translate(-50%, -50%)',
      },
    },
    {
      props: { anchorOrigin: 'bottom.right' },
      style: {
        bottom: `${0 + vertical}px`,
        right: `${0 + horizontal}px`,
        transform: 'translate(50%, 50%)',
      },
    },
    {
      props: { anchorOrigin: 'bottom.left' },
      style: {
        bottom: `${0 + vertical}px`,
        left: `${0 + horizontal}px`,
        transform: 'translate(-50%, 50%)',
      },
    },
  ];
};
